/**
 * Parallax banner
 *
 * An image or video banner with two optional scroll-driven effects, both
 * powered by the self-hosted GSAP build with the ScrollTrigger plugin:
 *
 *  1. Media parallax: the background drifts vertically at a fraction of the
 *     scroll speed while the banner passes through the viewport.
 *  2. Text fill: the banner is pinned and a full-opacity copy of the text is
 *     revealed one character at a time over the top of a faded copy.
 *
 * GSAP is only downloaded once the banner is near the viewport, and the
 * loader is shared with the scroll driven banner via window.themeGsapReady.
 */
(function () {
  if (customElements.get('parallax-banner')) return;

  /**
   * Loads GSAP and ScrollTrigger once per page, no matter how many
   * GSAP-driven components are on it.
   * @param {string} gsapUrl
   * @param {string} scrollTriggerUrl
   * @returns {Promise<object>}
   */
  function loadGsap(gsapUrl, scrollTriggerUrl) {
    if (window.themeGsapReady) return window.themeGsapReady;

    const loadScript = (src) => new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = resolve;
      script.onerror = () => reject(new Error(`Failed to load ${src}`));
      document.head.appendChild(script);
    });

    window.themeGsapReady = (window.gsap ? Promise.resolve() : loadScript(gsapUrl))
      .then(() => (window.ScrollTrigger ? null : loadScript(scrollTriggerUrl)))
      .then(() => {
        window.gsap.registerPlugin(window.ScrollTrigger);
        return window.gsap;
      });

    return window.themeGsapReady;
  }

  /**
   * Height of the sticky header, if there is one, so the pinned banner sits
   * below it rather than underneath it.
   * @returns {number}
   */
  function stickyHeaderOffset() {
    // The theme makes the header's section wrapper sticky on desktop only,
    // and adds this class once that behaviour has been initialised.
    const header = document.querySelector('.cc-header--sticky');
    if (!header) return 0;

    const position = window.getComputedStyle(header).position;
    if (position !== 'sticky' && position !== 'fixed') return 0;

    return Math.max(0, header.getBoundingClientRect().height - 1);
  }

  class ParallaxBanner extends HTMLElement {
    connectedCallback() {
      this.frame = this.querySelector('[data-parallax-frame]');
      this.media = this.querySelector('[data-parallax-media]');
      this.fillLayer = this.querySelector('[data-parallax-fill]');
      this.spacer = this.querySelector('[data-parallax-spacer]');
      if (!this.frame) return;

      this.scrollEffect = this.dataset.scrollEffect === 'true' && !!this.fillLayer && !!this.spacer;
      this.coverRelease = this.dataset.release === 'cover' && !!this.spacer;
      this.smoothing = parseFloat(this.dataset.scrollSmoothing) || 0;
      this.fillEnd = Math.min(1, Math.max(0.1, parseFloat(this.dataset.fillEnd) || 0.85));
      this.fillStyle = this.dataset.fillStyle === 'instant' ? 'instant' : 'fade';
      this.parallax = Math.max(0, parseFloat(this.dataset.parallax) || 0);

      if (!this.scrollEffect && this.parallax === 0) return;
      if (this.prefersReducedMotion()) return;

      this.loadWhenNear();
    }

    disconnectedCallback() {
      this.stopWatching();
      this.teardown();
    }

    prefersReducedMotion() {
      return this.dataset.reducedMotion === 'true'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    /**
     * Defers the GSAP download until the banner is close to the viewport.
     * 600px is enough for the pin to register before the banner reaches the
     * top, without parsing GSAP during the first screenful of scroll.
     */
    loadWhenNear() {
      const start = () => loadGsap(this.dataset.gsapUrl, this.dataset.scrollTriggerUrl)
        .then(() => this.init())
        .catch(() => {});

      if ('IntersectionObserver' in window === false) {
        start();
        return;
      }

      this.loadObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.stopWatching();
        start();
      }, { rootMargin: '600px 0px' });

      this.loadObserver.observe(this);
    }

    stopWatching() {
      if (!this.loadObserver) return;
      this.loadObserver.disconnect();
      this.loadObserver = null;
    }

    init() {
      if (!this.isConnected) return;

      this.teardown();
      this.triggers = [];

      if (this.scrollEffect) this.initFill();
      if (this.parallax > 0) this.initParallax();

      this.observeResize();
      window.ScrollTrigger.refresh();
    }

    /**
     * Scroll distance the banner stays pinned for while the text fills in.
     * Read from the spacer element so the fill and the sticky layout agree.
     * @returns {number}
     */
    pinnedLength() {
      return this.spacer.offsetHeight;
    }

    /**
     * Total scroll distance the banner stays pinned for. Cover mode keeps it
     * pinned for a further banner height while the next section slides over.
     * @returns {number}
     */
    pinTotal() {
      return this.pinnedLength() + (this.coverRelease ? this.frame.offsetHeight : 0);
    }

    /**
     * Makes the frame sticky below the header and keeps the CSS variables the
     * sticky layout depends on up to date
     *
     * @returns {void}
     */
    pin() {
      this.updatePinTop = () => {
        this.style.setProperty('--pb-pin-top', `${stickyHeaderOffset()}px`);
      };
      this.updatePinTop();
      window.ScrollTrigger.addEventListener('refreshInit', this.updatePinTop);

      if (this.coverRelease) {
        const updateFrameHeight = () => {
          this.style.setProperty('--pb-frame-height', `${this.frame.offsetHeight}px`);
        };
        // Set before --pinned so the cover hold is never briefly zero
        updateFrameHeight();
        this.frameObserver = new ResizeObserver(updateFrameHeight);
        this.frameObserver.observe(this.frame);
      }

      this.classList.add('parallax-banner--pinned');
    }

    /**
     * Pins the banner and reveals the fill layer character by character as
     * the visitor scrolls through the spacer. The fill ends with the spacer,
     * so a cover hold after it does not stretch the fill.
     */
    initFill() {
      const gsap = window.gsap;
      const chars = Array.from(this.fillLayer.querySelectorAll('.parallax-banner__char'));
      if (!chars.length) return;

      this.classList.add('parallax-banner--fill-ready');
      gsap.set(chars, { opacity: 0 });
      this.pin();

      const start = () => `top top+=${stickyHeaderOffset()}`;

      const step = this.fillEnd / chars.length;
      // With 'fade', each character overlaps the next so the fill reads as a
      // continuous sweep rather than a strobe. 'instant' snaps each one on.
      const duration = this.fillStyle === 'instant' ? 0.0001 : step * 2;

      this.fillTimeline = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          // The container, not the stuck frame, so positions measured during
          // a refresh are the same wherever the page is scrolled to
          trigger: this,
          start,
          end: () => `+=${this.pinnedLength()}`,
          scrub: this.smoothing > 0 ? this.smoothing : true,
          invalidateOnRefresh: true
        }
      });

      this.fillTimeline.to(chars, { opacity: 1, duration, stagger: step }, 0);
      // Pad the timeline so the finished text holds until the fill distance ends.
      this.fillTimeline.to({}, { duration: Math.max(0.0001, 1 - this.fillTimeline.duration()) });

      this.triggers.push(this.fillTimeline.scrollTrigger);
    }

    /**
     * Drifts the media vertically while the banner travels through the
     * viewport. The journey includes the pinned distance, if any, so the
     * drift keeps going while the banner is held in place.
     */
    initParallax() {
      const gsap = window.gsap;
      if (!this.media) return;

      this.classList.add('parallax-banner--parallax');

      const travel = () => {
        const pinned = this.scrollEffect ? this.pinTotal() : 0;
        return window.innerHeight + this.frame.offsetHeight + pinned;
      };

      this.parallaxTween = gsap.fromTo(this.media, { yPercent: -this.parallax }, {
        yPercent: this.parallax,
        ease: 'none',
        scrollTrigger: {
          trigger: this,
          start: 'top bottom',
          end: () => `+=${Math.round(travel())}`,
          scrub: true,
          invalidateOnRefresh: true
        }
      });

      this.triggers.push(this.parallaxTween.scrollTrigger);
    }

    observeResize() {
      if (this.resizeObserver) return;

      let width = window.innerWidth;
      let pageHeight = document.documentElement.offsetHeight;

      this.resizeObserver = new ResizeObserver(() => {
        // Page height catches sections above that grow after GSAP has
        // measured, e.g. a carousel initialising late
        const nextPageHeight = document.documentElement.offsetHeight;
        if (window.innerWidth === width && nextPageHeight === pageHeight) return;
        width = window.innerWidth;
        pageHeight = nextPageHeight;

        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => window.ScrollTrigger?.refresh(), 200);
      });

      this.resizeObserver.observe(document.documentElement);
    }

    teardown() {
      clearTimeout(this.resizeTimer);

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }

      if (this.frameObserver) {
        this.frameObserver.disconnect();
        this.frameObserver = null;
      }

      if (this.updatePinTop) {
        window.ScrollTrigger?.removeEventListener('refreshInit', this.updatePinTop);
        this.updatePinTop = null;
      }

      (this.triggers || []).forEach((trigger) => trigger?.kill(true));
      this.triggers = [];

      if (this.fillTimeline) {
        this.fillTimeline.kill();
        this.fillTimeline = null;
      }

      if (this.parallaxTween) {
        this.parallaxTween.kill();
        this.parallaxTween = null;
      }

      if (window.gsap) {
        if (this.media) window.gsap.set(this.media, { clearProps: 'transform' });
        if (this.fillLayer) {
          window.gsap.set(this.fillLayer.querySelectorAll('.parallax-banner__char'), { clearProps: 'opacity' });
        }
      }

      this.classList.remove('parallax-banner--fill-ready', 'parallax-banner--pinned', 'parallax-banner--parallax');
    }
  }

  customElements.define('parallax-banner', ParallaxBanner);
})();
